// Portfolio performance engine — pure, dependency-free, exhaustively testable.
//
// - TWR  : Modified-Dietz return per sub-period, geometrically linked. Strips the
//          effect of external cashflow timing -> comparable to a benchmark.
// - MWR  : money-weighted (XIRR) over the external cashflow series + start/end
//          value -> the client's actual annualised dollar return.
// Returns are decimals (0.0815 = 8.15%); money is integer cents. Only EXTERNAL
// cashflows are passed in here (internal buys/sells are excluded upstream).
//
// Total return incl. franking (decision 4): the optional `income` series folds
// grossed-up income into the period's gain. Phase 2 wires it empty; Phase 4
// supplies it from income_events.

export interface SeriesPoint { date: string; valueCents: number; }   // EOD AUD value
export interface CashFlow { date: string; amountCents: number; }     // +in / -out (external)

const DAY = 86_400_000;
const round6 = (n: number): number => Math.round(n * 1e6) / 1e6;

function toUTC(date: string): number {
  return Date.parse(`${date}T00:00:00Z`);
}
function daysBetween(a: string, b: string): number {
  return Math.round((toUTC(b) - toUTC(a)) / DAY);
}

/**
 * Modified Dietz return for one sub-period.
 * @param flows external flows during the period, each with `weight` in [0,1]
 *   = (period_days - days_from_start) / period_days (start-date flow weight 1).
 */
export function modifiedDietz(
  beginCents: number,
  endCents: number,
  flows: Array<{ weight: number; amountCents: number }>,
  incomeCents = 0,
): number {
  const sumFlows = flows.reduce((s, f) => s + f.amountCents, 0);
  const weighted = flows.reduce((s, f) => s + f.weight * f.amountCents, 0);
  const avgCapital = beginCents + weighted;
  if (avgCapital === 0) return 0;
  // `incomeCents` (e.g. franking credits not already in the cash value) adds to
  // the period gain numerator without changing the average capital base.
  return round6((endCents - beginCents - sumFlows + incomeCents) / avgCapital);
}

/** Geometrically link sub-period returns: Π(1+r) − 1. */
export function linkReturns(subReturns: number[]): number {
  return round6(subReturns.reduce((acc, r) => acc * (1 + r), 1) - 1);
}

export interface IndexPoint { date: string; index: number; }

export interface HouseholdSeriesResult {
  series: SeriesPoint[];
  /** Per household date: how many accounts had an actual same-day valuation vs a carried-forward one. */
  coverage: Array<{ date: string; accountsWithData: number; accountsCarried: number }>;
  /** Worst-case carried-forward count across the series (0 = every date fully fresh). */
  maxCarried: number;
}

/**
 * Consolidate multiple per-account EOD value series into ONE household series on
 * a union calendar. Summing only same-date snapshots makes a missing account
 * valuation look like a giant withdrawal; instead each account carries forward
 * its last known value, but only from its first valuation onward (an account
 * that hasn't started yet contributes nothing rather than a false zero).
 */
export function consolidateHouseholdSeries(
  perAccount: Array<{ accountId: string; points: SeriesPoint[] }>,
): HouseholdSeriesResult {
  const dates = new Set<string>();
  const sorted = perAccount.map((a) => ({
    accountId: a.accountId,
    points: [...a.points].sort((x, y) => toUTC(x.date) - toUTC(y.date)),
  }));
  for (const a of sorted) for (const p of a.points) dates.add(p.date);
  const calendar = [...dates].sort((x, y) => toUTC(x) - toUTC(y));

  const series: SeriesPoint[] = [];
  const coverage: HouseholdSeriesResult['coverage'] = [];
  let maxCarried = 0;

  for (const date of calendar) {
    let total = 0;
    let withData = 0;
    let carried = 0;
    for (const a of sorted) {
      const started = a.points.length > 0 && toUTC(a.points[0].date) <= toUTC(date);
      if (!started) continue;
      let last: SeriesPoint | null = null;
      let exact = false;
      for (const p of a.points) {
        if (toUTC(p.date) <= toUTC(date)) { last = p; exact = p.date === date; }
        else break;
      }
      if (last) {
        total += last.valueCents;
        if (exact) withData += 1; else carried += 1;
      }
    }
    series.push({ date, valueCents: total });
    coverage.push({ date, accountsWithData: withData, accountsCarried: carried });
    if (carried > maxCarried) maxCarried = carried;
  }

  return { series, coverage, maxCarried };
}

/**
 * Cashflow-adjusted TWR index — the correct basis for a "growth of $X" chart.
 * Indexing raw portfolio value makes contributions/withdrawals look like
 * performance; this compounds each Modified-Dietz sub-period return instead, so
 * the line reflects investment return only. Starts at `base` (default 100) on
 * the first series date.
 */
export function twrIndexSeries(
  series: SeriesPoint[],
  externalFlows: CashFlow[] = [],
  income: CashFlow[] = [],
  base = 100,
): IndexPoint[] {
  if (series.length === 0) return [];
  const pts = [...series].sort((a, b) => toUTC(a.date) - toUTC(b.date));
  const flows = [...externalFlows].sort((a, b) => toUTC(a.date) - toUTC(b.date));
  const out: IndexPoint[] = [{ date: pts[0].date, index: round6(base) }];
  let idx = base;
  for (let i = 1; i < pts.length; i += 1) {
    const begin = pts[i - 1];
    const end = pts[i];
    const periodDays = Math.max(1, daysBetween(begin.date, end.date));
    const inPeriod = flows
      .filter((f) => toUTC(f.date) > toUTC(begin.date) && toUTC(f.date) <= toUTC(end.date))
      .map((f) => ({
        amountCents: f.amountCents,
        weight: (periodDays - daysBetween(begin.date, f.date)) / periodDays,
      }));
    const incomeCents = income
      .filter((f) => toUTC(f.date) > toUTC(begin.date) && toUTC(f.date) <= toUTC(end.date))
      .reduce((sum, f) => sum + f.amountCents, 0);
    const r = modifiedDietz(begin.valueCents, end.valueCents, inPeriod, incomeCents);
    idx *= 1 + r;
    out.push({ date: end.date, index: round6(idx) });
  }
  return out;
}

/**
 * Time-weighted return across an EOD value series with external cashflows.
 * Each consecutive pair of points is one Modified-Dietz sub-period; flows in
 * (begin, end] are weighted by when in the sub-period they occurred.
 */
export function twrFromSeries(
  series: SeriesPoint[],
  externalFlows: CashFlow[] = [],
  income: CashFlow[] = [],
): number | null {
  if (series.length < 2) return null;
  const pts = [...series].sort((a, b) => toUTC(a.date) - toUTC(b.date));
  const flows = [...externalFlows].sort((a, b) => toUTC(a.date) - toUTC(b.date));
  const sub: number[] = [];
  for (let i = 1; i < pts.length; i += 1) {
    const begin = pts[i - 1];
    const end = pts[i];
    const periodDays = Math.max(1, daysBetween(begin.date, end.date));
    const inPeriod = flows
      .filter((f) => toUTC(f.date) > toUTC(begin.date) && toUTC(f.date) <= toUTC(end.date))
      .map((f) => ({
        amountCents: f.amountCents,
        weight: (periodDays - daysBetween(begin.date, f.date)) / periodDays,
      }));
    const incomeCents = income
      .filter((f) => toUTC(f.date) > toUTC(begin.date) && toUTC(f.date) <= toUTC(end.date))
      .reduce((sum, f) => sum + f.amountCents, 0);
    sub.push(modifiedDietz(begin.valueCents, end.valueCents, inPeriod, incomeCents));
  }
  return linkReturns(sub);
}

/**
 * XIRR — annualised money-weighted return. Sign convention: investments are
 * negative, returns positive (Actual/365). Newton–Raphson with a bisection
 * fallback. null if degenerate or no convergence.
 */
export function xirr(cashflows: CashFlow[]): number | null {
  if (cashflows.length < 2) return null;
  const cfs = [...cashflows].sort((a, b) => toUTC(a.date) - toUTC(b.date));
  const hasPos = cfs.some((c) => c.amountCents > 0);
  const hasNeg = cfs.some((c) => c.amountCents < 0);
  if (!hasPos || !hasNeg) return null;
  const t0 = toUTC(cfs[0].date);
  const years = (d: string): number => (toUTC(d) - t0) / (365 * DAY);
  const npv = (rate: number): number =>
    cfs.reduce((s, c) => s + c.amountCents / (1 + rate) ** years(c.date), 0);
  const dnpv = (rate: number): number =>
    cfs.reduce((s, c) => s - (years(c.date) * c.amountCents) / (1 + rate) ** (years(c.date) + 1), 0);

  // Newton–Raphson
  let rate = 0.1;
  for (let i = 0; i < 100; i += 1) {
    const f = npv(rate);
    const d = dnpv(rate);
    if (Math.abs(f) < 1e-6) return round6(rate);
    if (d === 0) break;
    const next = rate - f / d;
    if (!Number.isFinite(next) || next <= -0.999999) break;
    if (Math.abs(next - rate) < 1e-9) { rate = next; break; }
    rate = next;
  }
  if (Math.abs(npv(rate)) < 1e-3) return round6(rate);

  // Bisection fallback over (-0.9999, 100]
  let lo = -0.9999;
  let hi = 100;
  let flo = npv(lo);
  let fhi = npv(hi);
  if (flo * fhi > 0) return null;
  for (let i = 0; i < 200; i += 1) {
    const mid = (lo + hi) / 2;
    const fmid = npv(mid);
    if (Math.abs(fmid) < 1e-6) return round6(mid);
    if (flo * fmid < 0) { hi = mid; fhi = fmid; } else { lo = mid; flo = fmid; }
  }
  return round6((lo + hi) / 2);
}

/** Money-weighted return from a value series + external flows. */
export function mwrFromSeries(series: SeriesPoint[], externalFlows: CashFlow[] = []): number | null {
  if (series.length < 2) return null;
  const pts = [...series].sort((a, b) => toUTC(a.date) - toUTC(b.date));
  const start = pts[0];
  const end = pts[pts.length - 1];
  // Investor cashflow = -(portfolio flow): start value is an investment (-), a
  // contribution (+ to portfolio) is a further investment (-), the end value is
  // a redemption (+).
  const cfs: CashFlow[] = [{ date: start.date, amountCents: -start.valueCents }];
  for (const f of externalFlows) {
    if (toUTC(f.date) > toUTC(start.date) && toUTC(f.date) <= toUTC(end.date)) {
      cfs.push({ date: f.date, amountCents: -f.amountCents });
    }
  }
  cfs.push({ date: end.date, amountCents: end.valueCents });
  return xirr(cfs);
}

/* ----------------------------- Periods ----------------------------- */

export type PeriodKey = '1m' | '3m' | '6m' | '1y' | '3y' | '5y' | 'fytd' | 'cytd' | 'itd';
export const PERIOD_KEYS: readonly PeriodKey[] = ['1m', '3m', '6m', '1y', '3y', '5y', 'fytd', 'cytd', 'itd'];

function addMonths(date: string, delta: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  const targetMonth = d.getUTCMonth() + delta;
  const y = d.getUTCFullYear() + Math.floor(targetMonth / 12);
  const m = ((targetMonth % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const day = Math.min(d.getUTCDate(), lastDay);
  return new Date(Date.UTC(y, m, day)).toISOString().slice(0, 10);
}

/** Period start date for a period ending on `asOf`. 'itd' returns null (use series start). */
export function periodStart(asOf: string, period: PeriodKey): string | null {
  const d = new Date(`${asOf}T00:00:00Z`);
  switch (period) {
    case '1m': return addMonths(asOf, -1);
    case '3m': return addMonths(asOf, -3);
    case '6m': return addMonths(asOf, -6);
    case '1y': return addMonths(asOf, -12);
    case '3y': return addMonths(asOf, -36);
    case '5y': return addMonths(asOf, -60);
    case 'cytd': return `${d.getUTCFullYear()}-01-01`;
    case 'fytd': {
      // Australian FY starts 1 July.
      const y = d.getUTCMonth() + 1 >= 7 ? d.getUTCFullYear() : d.getUTCFullYear() - 1;
      return `${y}-07-01`;
    }
    case 'itd':
    default:
      return null;
  }
}

export interface PeriodReturn {
  twr: number | null;
  mwr: number | null;
  startDate: string | null;
  endDate: string;
  days: number;
}

/** Annualise a cumulative return when the window exceeds a year. */
export function annualise(totalReturn: number, days: number): number {
  if (days <= 366) return round6(totalReturn);
  return round6((1 + totalReturn) ** (365 / days) - 1);
}

/**
 * Returns for one named period. Anchors the opening value to the last series
 * point on/before the period start (so the first sub-period captures the move
 * from the period boundary). >1y windows are annualised.
 */
export function periodReturn(
  series: SeriesPoint[],
  externalFlows: CashFlow[],
  asOf: string,
  period: PeriodKey,
  income: CashFlow[] = [],
): PeriodReturn {
  const pts = [...series].sort((a, b) => toUTC(a.date) - toUTC(b.date)).filter((p) => toUTC(p.date) <= toUTC(asOf));
  const start = periodStart(asOf, period);
  let window: SeriesPoint[];
  if (start == null) {
    window = pts;
  } else {
    const anchor = [...pts].reverse().find((p) => toUTC(p.date) <= toUTC(start));
    const after = pts.filter((p) => toUTC(p.date) > toUTC(start));
    window = anchor ? [anchor, ...after] : after;
  }
  if (window.length < 2) {
    return { twr: null, mwr: null, startDate: window[0]?.date ?? start, endDate: asOf, days: 0 };
  }
  const winStart = window[0].date;
  const winEnd = window[window.length - 1].date;
  const days = daysBetween(winStart, winEnd);
  const flows = externalFlows.filter((f) => toUTC(f.date) > toUTC(winStart) && toUTC(f.date) <= toUTC(winEnd));
  const incomeWin = income.filter((f) => toUTC(f.date) > toUTC(winStart) && toUTC(f.date) <= toUTC(winEnd));
  const twrRaw = twrFromSeries(window, flows, incomeWin);
  const mwr = mwrFromSeries(window, flows);
  return {
    twr: twrRaw == null ? null : annualise(twrRaw, days),
    mwr,
    startDate: winStart,
    endDate: winEnd,
    days,
  };
}

/** Returns for every period key (or a subset). */
export function periodReturns(
  series: SeriesPoint[],
  externalFlows: CashFlow[],
  asOf: string,
  income: CashFlow[] = [],
  periods: readonly PeriodKey[] = PERIOD_KEYS,
): Record<string, PeriodReturn> {
  const out: Record<string, PeriodReturn> = {};
  for (const p of periods) out[p] = periodReturn(series, externalFlows, asOf, p, income);
  return out;
}
